import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { PolicyService } from '@services/policy.service';
import Swal from 'sweetalert2'
import { CoreModule } from 'app/modules/core.module';
import { GroupOfPoliceService } from '@services/groupOfPolicies.service';
import { ContractService } from '@services/contract.service';
import { PositionService } from '@services/position.service';
import { EntityService } from '@services/entity.service';
import { RequestService } from '@services/request.service';
import { StateDisplayComponent } from 'app/shared/state-display/state-display.component';
import { PolicyInformationComponent } from '../incorporation/policy-information/policy-information.component';
import { apiResponse, apiLog, apiError } from 'app/shared/api-response';

@Component({
  selector: 'app-contracted-policies',
  standalone: true,
  imports: [CoreModule, NgbToastModule, StateDisplayComponent, PolicyInformationComponent],
  providers: [PolicyService, GroupOfPoliceService, EntityService, ContractService, PositionService, RequestService],
  templateUrl: './contracted-policies.component.html',
  styleUrls: ['./contracted-policies.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractedPoliciesComponent {

  @Input() insured: any

  @Output() updatePolicySelected = new EventEmitter()

  private _entityService = inject(EntityService)
  private _groupPolicyService = inject(GroupOfPoliceService)
  private _positionService = inject(PositionService)
  private _requestService = inject(RequestService)
  private _modalService = inject(NgbModal)
  private _config = inject(NgbModalConfig)
  _contractService = inject(ContractService)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)


  protected listContractedPolicies: any[] = []
  protected listPolicies: any[] = []
  protected policySelected: any
  protected showSpinner = true
  protected policiesDestiny: any[] = []
  protected listEntities = []
  protected listGroups = []
  protected listPositions: any[] = []
  protected showToast: any
  protected editableCapital = false

  public transferForm: any
  public transferInsured = false
  public showIncorporationForm = false
  public existingCoverageIds: string[] = []
  public existingEntityIds: string[] = []

  public actualContract: any

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true
  })
  public action: any
  constructor(
    private _policyService: PolicyService,
    private fb: FormBuilder,
  ) {

  }

  private modalOptions = {
    size: 'lg',
    scrollable: true,
    animation: true,
  }

  ngOnInit(): void {
    this._config.backdrop = 'static';
    this._config.keyboard = false;
    this.transferForm = this.fb.group({
      insuredId: [null, Validators.required],
      coverageId: [null, Validators.required],
      relationshipId: [null, Validators.required],
      jobTitleId: [null, Validators.required],
      entityId: [null, Validators.required],
      policyId: [null, Validators.required],
      groupId: [null, Validators.required],
      capital: [null, Validators.required],
      dateStart: [null, Validators.required]
    })

    this.getActiveContracts()
    this.getEntities()
  }

  getActiveContracts() {
    this._contractService.getActiveContracts(this.insured._id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de pólizas activas')
        this.listContractedPolicies = res.map((item: any) => {
        return {
          policyId: item.policyId,
          code: item.policy.code,
          name: item.policy.name,
          entity: item.policy.entity.name,
          entityId: item.policy.entity?._id,
          jobTitleId: item.jobTitleId,
          year: item.policy.year,
          dateStart: item.dateStart,
          dateFinish: item.dateFinish,
          relationship: item.relationship,
          coverageId: item.policy?.coverage?._id ?? item.policy?.coverageId,
        }
      })
      this.existingCoverageIds = [...new Set(this.listContractedPolicies.map(p => p.coverageId).filter(Boolean))]
      this.existingEntityIds = [...new Set(this.listContractedPolicies.map(p => p.entityId).filter(Boolean))]
      this.getHistoryContracts(this.listContractedPolicies[0])
      this.cdr.markForCheck()
    })
  }

  getHistoryContracts(selected: any) {
    this.policySelected = selected
    this.showSpinner = true
    if (!selected) {
      this.showSpinner = false
      return
    }
    this._contractService.getHistoryContracts(this.insured._id, selected.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de histórico de contratos')
      this.listPolicies = res
      this.showSpinner = false
      this.updatePolicySelected.emit(this.policySelected)
      this.cdr.markForCheck()
    })

  }

  createTransfer() {
    this.transferInsured = true
    const currentPolicy = this.listPolicies.find(p => p.enabled == true)
    this.actualContract = currentPolicy
    this.transferForm.patchValue({
      insuredId: this.insured._id,
      coverageId: currentPolicy?.coverageId ?? currentPolicy?.policy?.coverageId,
      relationshipId: currentPolicy?.relationshipId,
      entityId: currentPolicy?.policy?.entityId,
      policyId: currentPolicy?.policyId,
      groupId: currentPolicy?.groupId,
      capital: currentPolicy?.capital,
      dateStart: new Date().toISOString().split('T')[0],
    })
    this.listGroups = []
    this.listPositions = []
    this._policyService.getPolicyTransfer(this.transferForm.value.entityId, this.actualContract.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de pólizas destino')
      this.policiesDestiny = res
      this.cdr.markForCheck()
    })
    this._groupPolicyService.getByPolicy(this.transferForm.value.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de grupos')
      const currentGroupId = this.actualContract?.groupId
      if (currentGroupId && !res.some((g: any) => g._id === currentGroupId)) {
        res = [this.actualContract.group, ...res]
      }
      this.listGroups = res
      this.cdr.markForCheck()
    })
    this._positionService.getByGroup(this.transferForm.value.groupId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de cargos')
      this.listPositions = res
      this.cdr.markForCheck()
    })
  }

  getEntities() {
    this._entityService.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de entidades')
      this.listEntities = res
      this.cdr.markForCheck()
    })
  }

  getPoliciesDestiny() {
    this.resetFields()
    this._policyService.getPolicyTransfer(this.transferForm.value.entityId, this.actualContract.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de pólizas destino')
      this.policiesDestiny = res
      this.cdr.markForCheck()
    })
  }

  resetFields() {
    this.transferForm.patchValue({ policyId: null, groupId: null, coverageId: null, capital: null, jobTitleId: null })
    this.listPolicies = []
    this.listGroups = []
    this.listPositions = []
  }


  getGroupOfPolicy(item: any) {
    this.transferForm.patchValue({ policyId: item._id, coverageId: item.coverageId })
    this._groupPolicyService.getByPolicy(this.transferForm.value.policyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de grupos')
      this.listGroups = res
      this.cdr.markForCheck()
    })
  }

  getPositions() {
    this._positionService.getByGroup(this.transferForm.value.groupId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de cargos')
      this.listPositions = res
      this.cdr.markForCheck()
    })
  }

  setCapital(position: any) {
    this.editableCapital = position.editable ?? false
    this._positionService.setCapital(this.transferForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      apiLog(res, 'Carga de capital')
      this.transferForm.patchValue({ capital: res.capital })
      this.cdr.markForCheck()
    })
  }

  cancel() {
    this.transferInsured = false
    this.transferForm.reset()
  }

  save() {
    if (this.transferForm.valid) {
      this._requestService.modify(this.transferForm.value, this.actualContract).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res) => {
          apiResponse(res, 'Modificación de póliza')
          this.cancel()
          this.getHistoryContracts(this.policySelected)
        },
        error: (err) => apiError(err, 'Error al modificar póliza')
      })
    }
  }

  openNewPolicyModal(content: any) {
    this._modalService.open(content, this.modalOptions)
  }

  cancelIncorporation() {
    this.showIncorporationForm = false
  }

  saveIncorporation(data: any) {
    this._requestService.incorporation(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        apiResponse(res, 'Incorporación generada')
        this._modalService.dismissAll()
        this.showIncorporationForm = false
        this.getActiveContracts()
        this.cdr.markForCheck()
      },
      error: (err) => apiError(err, 'Error al generar incorporación')
    })
  }
 
}
